/**
 * Submit Artifact Tool
 * 
 * Creates an exportable artifact (chart or range) for insertion into PowerPoint.
 * Generates both image format (for Office.js) and mini-workbook (for OLE/COM).
 * 
 * The artifact is stored in globalStateManager's contextResolver for later use.
 */

import { ToolBase } from "../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import GlobalStateManager from "../../../../globals/global-state/globalStateManager";
import { generateContextID } from "../../../../utils/idGenerator";
import { 
    SubmitArtifactParamsSchema, 
    type SubmitArtifactParams, 
    type SubmitArtifactResponse 
} from "./submitArtifact.schema";
import { DEFAULT_EXCEL_API_BASE_URL } from "../../constants";
import { fetchWithRetry } from "../../utils/fetchWithRetry";

// ═══════════════════════════════════════════════════════════════════════════
// TOOL DESCRIPTION
// ═══════════════════════════════════════════════════════════════════════════

const SUBMIT_ARTIFACT_DESCRIPTION = `Export a chart or range as an artifact for insertion into PowerPoint.

## When to Use

Call this tool when you've finished creating or modifying content that the user wants 
to insert into their presentation. This creates both:
- A PNG image (for cross-platform insertion)
- A mini-workbook (for editable OLE insertion on Windows)

## Artifact Types

### Chart Export
Export a chart with its data. The chart name is the identifier (e.g., "Chart 1", "Revenue Chart").

\`\`\`json
{
  "artifact_type": "chart",
  "sheet_name": "Dashboard",
  "chart_name": "Revenue Chart",
  "description": "Q3 2024 Revenue by Region"
}
\`\`\`

### Range Export
Export a cell range as a table/data image.

\`\`\`json
{
  "artifact_type": "range",
  "sheet_name": "Data",
  "range_ref": "A1:F20",
  "description": "Quarterly summary table"
}
\`\`\`

## Export Options (User Settings)

Export formatting options (black & white, collapse groups, clear comments) are configured 
by the user in Settings > Excel Export. These cannot be overridden per-export - they apply 
consistently to all exports based on user preference.

## What Happens

1. The content is exported as a PNG image
2. A mini-workbook is created containing just the exported content
3. Both are stored with metadata linking back to the source file
4. The response includes:
   - artifact_id: Use this to insert the artifact
   - aspect_ratio: The width/height ratio (e.g., 1.5 means 50% wider than tall)
   - width_points, height_points: Exact dimensions in points
5. The artifact can then be inserted into PowerPoint as either:
   - A static image (Office.js) - uses aspect_ratio to preserve proportions
   - An editable Excel object (OLE/COM on Windows)

## Response

The response includes the artifact's aspect_ratio (width/height). When the orchestrator 
inserts this artifact as an image, it only needs to specify ONE of width or height - 
the other dimension is auto-calculated to preserve the aspect ratio.

## Description

Always provide a meaningful description - this becomes the citation text when the 
artifact is inserted into the presentation, and helps users understand what data 
the shape contains when they right-click to view sources.`;

// ═══════════════════════════════════════════════════════════════════════════
// EXCEL ARTIFACT INTERFACE (for storage in contextResolver)
// ═══════════════════════════════════════════════════════════════════════════

export interface ExcelArtifact {
    /** Unique artifact ID (artifact_xxx format) */
    artifactId: string;
    
    /** Citation ID for metadata system (excel_xxx format) */
    excelId: string;
    
    /** Type of artifact */
    artifactType: "chart" | "range";
    
    /** Source tracking for re-sync */
    source: {
        originalFilePath: string;
        sheetName: string;
        sourceRef: string;  // Chart name or range
    };
    
    /** PNG image data (base64) */
    imageBase64: string;
    
    /** Image format */
    imageFormat: string;
    
    /** Path to mini-workbook for OLE */
    oleFilePath: string;
    
    /** Export options used */
    exportOptions: {
        blackAndWhite: boolean;
        collapseGroups: boolean;
        clearComments: boolean;
    };
    
    /** Preview image (base64) */
    previewBase64?: string;
    
    /** Human-readable description */
    description?: string;
    
    /** When created (Unix timestamp) */
    createdAt: number;
    
    /** Width of the artifact in points (for aspect ratio preservation) */
    widthPoints?: number;
    
    /** Height of the artifact in points (for aspect ratio preservation) */
    heightPoints?: number;
    
    /** Aspect ratio (width/height) for dimension calculation during insertion */
    aspectRatio?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const SUBMIT_ARTIFACT_SYSTEM_PROMPT = `### submitArtifact
Export a chart or range for insertion into PowerPoint.

**When to use:**
- After creating/modifying content that needs to go into slides
- Creates PNG image + mini-workbook for insertion

**Artifact types:**
\`\`\`json
// Chart export
{
  "artifact_type": "chart",
  "sheet_name": "Dashboard",
  "chart_name": "Revenue Chart",
  "description": "Q3 2024 Revenue by Region"
}

// Range export
{
  "artifact_type": "range",
  "sheet_name": "Data",
  "range_ref": "A1:F20",
  "description": "Quarterly summary table"
}
\`\`\`

Returns artifact_id (artifact_xxx) for use with insertArtifacts tool.`;

// ═══════════════════════════════════════════════════════════════════════════
// SUBMIT ARTIFACT TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class SubmitArtifactTool extends ToolBase<SubmitArtifactParams, SubmitArtifactResponse> {
    public readonly name = "submitArtifact";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = SUBMIT_ARTIFACT_SYSTEM_PROMPT;
    
    // Reference to session (set by parent agent)
    private sessionId: string | null = null;
    private apiBaseUrl: string;
    
    // Streaming field configuration
    fieldsToCheck = [
        { name: "artifact_type", type: "string" as const, list_status: false, order: 1 },
        { name: "sheet_name", type: "string" as const, list_status: false, order: 2 },
        { name: "chart_name", type: "string" as const, list_status: false, order: 3 },
        { name: "range_ref", type: "string" as const, list_status: false, order: 4 },
        { name: "description", type: "string" as const, list_status: false, order: 5 },
    ];

    constructor(apiBaseUrl: string = DEFAULT_EXCEL_API_BASE_URL) {
        super();
        this.apiBaseUrl = apiBaseUrl;
        this.description = new ToolDescription(
            "submitArtifact",
            SUBMIT_ARTIFACT_DESCRIPTION,

            SubmitArtifactParamsSchema
        );
    }
    
    /**
     * Set the session ID for this tool (called by parent agent)
     */
    public setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    public async execute(parameters: unknown): Promise<SubmitArtifactResponse> {
        // Validate parameters
        const parseResult = safeParseToolParams(SubmitArtifactParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }
        
        const params = parseResult.data;
        
        if (!this.sessionId) {
            throw new Error("No active session. The agent must create a session first.");
        }
        
        // Get export options from settings ONLY - LLM cannot override these
        // Users configure export formatting in Settings > Excel Export
        const globalState = GlobalStateManager.getInstance();
        const excelSettings = globalState.getExcelExportSettings();
        
        // Use settings directly - no LLM override allowed
        const exportOptions = {
            black_and_white: excelSettings.defaultBlackAndWhite,
            collapse_groups: excelSettings.defaultCollapseGroups,
            clear_comments: excelSettings.defaultClearComments ?? false,
        };
        
        console.log(`[SubmitArtifactTool] Export options from settings: B&W=${excelSettings.defaultBlackAndWhite}, Collapse=${excelSettings.defaultCollapseGroups}, ClearComments=${excelSettings.defaultClearComments}`);
        
        // Generate artifact ID using TypeScript's collision-safe system
        // This ensures proper ID management through contextResolver
        const artifactId = generateContextID('artifact');
        const excelId = generateContextID('excel');
        const artifactLocator =
            params.artifact_type === "chart"
                ? { chart_name: params.chart_name }
                : { range_ref: params.range_ref };
        
        console.log(`[SubmitArtifactTool] Creating ${params.artifact_type} artifact (${artifactId}) from session ${this.sessionId}`);
        
        try {
            // Call Python backend to create artifact
            // Pass the pre-generated IDs so Python uses them instead of generating its own
            const response = await fetchWithRetry(`${this.apiBaseUrl}/excel/artifact/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: this.sessionId,
                    sheet_name: params.sheet_name,
                    artifact_type: params.artifact_type,
                    ...artifactLocator,
                    export_options: exportOptions,  // Use settings-based options (no LLM override)
                    description: params.description,
                    // Pre-generated IDs from TypeScript's collision-safe system
                    artifact_id: artifactId,
                    excel_id: excelId,
                }),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[SubmitArtifactTool] API error: ${response.status} - ${errorText}`);
                throw new Error(`API error: ${response.status} - ${errorText}`);
            }
            
            const result: SubmitArtifactResponse = await response.json();
            
            if (!result.success) {
                throw new Error(result.error || "Failed to create artifact");
            }
            
            // Store artifact in globalStateManager's contextResolver
            await this.storeArtifact(result);
            
            console.log(`[SubmitArtifactTool] Created artifact ${result.artifact_id} (excel_id: ${result.excel_id})`);
            
            // ═══════════════════════════════════════════════════════════════
            // IMPORTANT: Do NOT return image_base64 in tool result!
            // Images bloat context window (can exceed 200K token limit).
            // Instead, store in contextResolver and return _imageIds for
            // automatic injection in the next user message.
            // ═══════════════════════════════════════════════════════════════
            const imageIds: string[] = [];
            if (result.image_base64) {
                // Store a preview image for LLM vision (separate from artifact storage)
                // Use async version to automatically resize if exceeds limits
                const previewImageId = `artifact_preview_${result.artifact_id}`;
                const processed = await globalState.contextResolver.storeImageAsync(
                    previewImageId,
                    result.image_base64,
                    'image/png',
                    {
                        artifactId: result.artifact_id,
                        artifactType: result.artifact_type,
                        description: result.description,
                    }
                );
                imageIds.push(previewImageId);
                console.log(`[SubmitArtifactTool] Stored preview image for LLM vision: ${previewImageId}${processed.wasResized ? ' (resized)' : ''}`);
            }
            
            // Return response WITHOUT base64 data - use _imageIds convention
            return {
                success: result.success,
                artifact_id: result.artifact_id,
                excel_id: result.excel_id,
                artifact_type: result.artifact_type,
                source: result.source,
                // NO image_base64 here! Would bloat context window
                image_format: result.image_format,
                ole_file_path: result.ole_file_path,
                export_options: result.export_options,
                description: result.description,
                created_at: result.created_at,
                warnings: result.warnings,
                // Signal for MessageBuilder to inject images in next user message
                _imageIds: imageIds.length > 0 ? imageIds : undefined,
            };
            
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error(`[SubmitArtifactTool] Error:`, error);
            throw new Error(errorMsg);
        }
    }
    
    /**
     * Store the artifact in globalStateManager's contextResolver
     */
    private async storeArtifact(result: SubmitArtifactResponse): Promise<void> {
        const globalState = GlobalStateManager.getInstance();
        
        // Create the artifact object for storage
        const artifact: ExcelArtifact = {
            artifactId: result.artifact_id,
            excelId: result.excel_id,
            artifactType: result.artifact_type,
            source: {
                originalFilePath: result.source.file,
                sheetName: result.source.sheet,
                sourceRef: result.source.ref,
            },
            imageBase64: result.image_base64,
            imageFormat: result.image_format,
            oleFilePath: result.ole_file_path,
            exportOptions: {
                blackAndWhite: result.export_options?.black_and_white || false,
                collapseGroups: result.export_options?.collapse_groups || true,
                clearComments: result.export_options?.clear_comments || false,
            },
            previewBase64: result.preview_base64,
            description: result.description,
            createdAt: result.created_at,
            widthPoints: result.width_points,
            heightPoints: result.height_points,
            aspectRatio: result.aspect_ratio,
        };
        
        // Store in context resolver using artifact prefix
        // This makes it available for later retrieval
        globalState.addOrUpdateContext(result.artifact_id, artifact);
        
        // Also store image in image cache for easy access
        // Use async version to automatically resize if exceeds limits
        if (result.image_base64) {
            await globalState.contextResolver.storeImageAsync(
                result.artifact_id,
                result.image_base64,
                'image/png',
                {
                    excelId: result.excel_id,
                    artifactType: result.artifact_type,
                    source: result.source,
                }
            );
        }
        
        console.log(`[SubmitArtifactTool] Stored artifact in globalStateManager: ${result.artifact_id}`);
    }
}

export default SubmitArtifactTool;



